
import { useParams, Link } from "react-router-dom";
import { Shell } from "@/components/layout/Shell";
import { ContractTimeline } from "@/components/contracts/timeline/ContractTimeline";
import { useContracts } from "@/hooks/useContracts";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ChevronLeft, DollarSign } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "@/lib/formatters";
import { useCurrencies } from "@/hooks/useCurrencies";

export default function ContractTimelinePage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { useContractQuery } = useContracts();
  const { data: contract, isLoading, error } = useContractQuery(id || "");
  const { useCurrencyQuery } = useCurrencies();
  
  const { data: currency } = useCurrencyQuery(contract?.currencyId || '');

  if (error) {
    return (
      <Shell>
        <Alert variant="destructive">
          <AlertDescription>
            {t('timeline.error', 'Error loading contract cost history.')}
          </AlertDescription>
        </Alert>
      </Shell>
    );
  }

  const currentCostDisplay = contract && currency ? 
    formatCurrency(contract.expectedCost, currency.code) : 
    contract?.expectedCost?.toLocaleString();

  const timelineTitle = contract?.type?.direction === 'incoming' 
    ? t('timeline.incomeTitle', 'Income History')
    : t('timeline.costTitle', 'Cost History');

  return (
    <Shell>
      <div className="space-y-6">
        <div className="flex justify-between items-center flex-wrap gap-4">
          <div className="flex items-center space-x-2">
            <Link to={`/contracts/${id}`} className="inline-flex items-center text-muted-foreground hover:text-foreground">
              <ChevronLeft className="h-6 w-6" />
            </Link>
            {isLoading ? (
              <Skeleton className="h-8 w-64" />
            ) : (
              <h1 className="text-3xl font-bold tracking-tight">
                {contract?.supplierName} - {timelineTitle}
              </h1>
            )}
          </div>
          <p className="text-muted-foreground mt-2 flex items-center gap-2">
            {contract?.type?.direction === 'incoming' 
              ? t('timeline.subtitleIncome', 'View the history of income changes made to this contract')
              : t('timeline.subtitleCost', 'View the history of cost changes made to this contract')
            }
            {!isLoading && contract && (
              <span className="inline-flex items-center bg-primary/10 text-primary px-2 py-1 rounded text-sm">
                <DollarSign className="h-4 w-4 mr-1" />
                {t('timeline.current', 'Current')}: {currentCostDisplay}
              </span>
            )}
          </p>
        </div>

        {id && <ContractTimeline contractId={id} />}
      </div>
    </Shell>
  );
}
